import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { filter, tap } from 'rxjs';

import { QUERY_KEY_CONST } from '@/constant';
import {
  useInfiniteTaskListQuery,
  useSubscriptions,
  useTaskRemoveQuery,
} from '@/hook';
import type { Task } from '@/type';

import { useWebSocket } from '../webSocket';
import { TaskListContext } from './context';
import type {
  TaskIDOrTask,
  TaskListContextValue,
  TaskListProviderProps,
} from './type';

const toTaskID = <T extends Task | Task['id']>(value: T) =>
  typeof value === 'number' ? value : value.id;

const TaskListContextProvider = ({
  children,

  projectID,
}: React.PropsWithChildren<TaskListProviderProps>) => {
  const selectedTaskIDMap = useRef<{
    [taskID: Task['id']]: boolean;
  }>({});
  const taskMap = useRef<{
    [taskID: Task['id']]: Task;
  }>({});
  const [selectedTaskIDList, setSelectedTaskIDList] = useState<Task['id'][]>(
    [],
  );
  const selectedTaskList = useMemo(
    // eslint-disable-next-line react-hooks/refs
    () => selectedTaskIDList.map(id => taskMap.current[id]),
    [selectedTaskIDList],
  );
  const queryClient = useQueryClient();
  const {
    data: taskListData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteTaskListQuery({ projectID });

  const { alarmMessage$ } = useWebSocket();

  useSubscriptions(() => {
    if (!alarmMessage$) return [];

    return [
      alarmMessage$
        .pipe(
          filter(e => e.alarmType === 'MiriTaskGenerateDone'),
          tap(() => {
            queryClient.invalidateQueries({
              queryKey: QUERY_KEY_CONST.TASK_KEYS.getList(),
            });
          }),
        )
        .subscribe(),
    ];
  }, [alarmMessage$]);

  const taskList = useMemo(() => {
    const taskList =
      taskListData?.pages.map(page => page.data.resultData).flat() ?? [];

    const newTaskMap = {} as {
      [taskID: number]: Task;
    };

    taskList.forEach(task => {
      newTaskMap[task.id] = task;
    });

    // eslint-disable-next-line react-hooks/refs
    taskMap.current = newTaskMap;

    return taskList;
  }, [taskListData]);

  const { mutateAsync: removeTask } = useTaskRemoveQuery();

  const refreshSelectedIDList = () => {
    const selectedTaskIDList = Object.keys(selectedTaskIDMap.current).map(
      Number,
    );

    setSelectedTaskIDList(selectedTaskIDList);
  };

  const selectTask = (target: TaskIDOrTask) => {
    const id = toTaskID(target);

    selectedTaskIDMap.current[id] = true;
  };

  return (
    <TaskListContext.Provider<TaskListContextValue>
      value={{
        hasNextPage,
        isFetchingNextPage,
        selectedTaskIDList,
        selectedTaskList,
        taskList,

        fetchNextPage: () => fetchNextPage().then(() => {}),

        selectTask(target) {
          selectedTaskIDMap.current = {};
          selectTask(target);

          refreshSelectedIDList();
        },
        selectTaskList(targetList) {
          selectedTaskIDMap.current = {};
          targetList.forEach(selectTask);

          refreshSelectedIDList();
        },

        removeTask: target =>
          removeTask(toTaskID(target)).then(res => res.data),
        removeTaskList: targetList =>
          Promise.allSettled(
            targetList
              .map(toTaskID)
              .map(id => removeTask(id).then(res => res.data)),
          ),
      }}
    >
      {children}
    </TaskListContext.Provider>
  );
};

export default TaskListContextProvider;
